import { useCallback, useRef } from "react";
import isEqual from "lodash.isequal";
import { PlaceholderExtension } from "@remirror/extension-placeholder";
import { LinkExtension } from "@remirror/extension-link";
import { wysiwygPreset } from "@remirror/preset-wysiwyg";
import {
  EditorComponent,
  Remirror,
  ThemeProvider,
  useRemirror,
  useEditorEvent,
  useHelpers,
} from "@remirror/react";
import { FloatingToolbar } from "@remirror/react-ui";
import { AllStyledComponent } from "@remirror/styles/emotion";

import type { UseThemeProps } from "@remirror/react";
import {
  RemirrorContentType,
  FrameworkProps,
  AnyExtension,
  CreateEditorStateProps,
  EditorView,
  RemirrorJSON,
  InvalidContentHandler,
  PlainExtension,
} from "@remirror/core";

import { CustomImageExtension } from "../../extensions";
import "./WysiwygEditor.css";

export type ContentType = RemirrorContentType;

class PasteHandlerExtension extends PlainExtension {
  get name() {
    return "pasteHandler" as const;
  }

  createPlugin() {
    return {
      props: {
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          if (event.clipboardData) {
            const text = event.clipboardData.getData("text");
            const { state, dispatch } = view;

            const isURL = /^(https?:\/\/[^\s]+)/.test(text);
            const { from, to } = state.selection;
            const hasTextSelection = from !== to;

            if (dispatch && isURL && hasTextSelection) {
              const linkMark = state.schema.marks.link.create({ href: text });
              const linkText = state.doc.textBetween(from, to);
              const textNode = state.schema.text(linkText, [linkMark]);

              // Replace the selected text with the link mark
              dispatch(state.tr.replaceWith(from, to, textNode));
              return true;
            }

            return false;
          }

          return false; // Let ProseMirror handle the event if clipboardData is not available
        },
      },
    };
  }
}

export interface ReactEditorProps<T extends AnyExtension>
  extends Pick<CreateEditorStateProps, "stringHandler">,
    Pick<FrameworkProps<T>, "editable" | "autoFocus" | "classNames"> {
  placeholder?: string;
  theme?: UseThemeProps["theme"];
  initialContent: RemirrorContentType | undefined;
  onBlur: (data: RemirrorContentType) => void;
  nodeId: string;
  canvasId: string;
  onImageUploadStart: () => void;
  onImageUploadError: () => void;
}

export function WysiwygEditor<T extends AnyExtension>({
  placeholder,
  stringHandler,
  theme,
  initialContent,
  canvasId,
  onImageUploadStart,
  onImageUploadError,
  onBlur,
  editable,
  ...rest
}: ReactEditorProps<T>) {
  const extensions = useCallback(() => {
    const linkExtension = new LinkExtension({
      autoLink: true,
      defaultTarget: "_blank",
      defaultProtocol: "https:",
    });
    linkExtension.addHandler("onClick", (_, data) => {
      const newTab = window.open(data.href, "_blank");
      newTab?.focus();
      return true;
    });

    return [
      new PlaceholderExtension({ placeholder }),
      linkExtension,
      new PasteHandlerExtension(),
      new CustomImageExtension({
        canvasId,
        onImageUploadStart,
        onImageUploadError,
        enableResizing: true,
      }),
      ...wysiwygPreset(), // This must go last to ensure our custom extensions are not overwritten by the preset
    ];
  }, [canvasId, placeholder, onImageUploadError, onImageUploadStart]);

  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers }) => {
      // Automatically remove all invalid nodes and marks.
      return transformers.remove(json, invalidContent);
    },
    [],
  );

  const { manager, state } = useRemirror({
    extensions,
    stringHandler,
    content: initialContent,
    onError,
  });

  return (
    <AllStyledComponent>
      <ThemeProvider theme={theme}>
        <Remirror
          manager={manager}
          initialContent={state}
          editable={editable}
          {...rest}
        >
          {editable && <FloatingToolbar />}
          <Editor initialContent={initialContent} onBlur={onBlur} />
        </Remirror>
      </ThemeProvider>
    </AllStyledComponent>
  );
}

function Editor({
  onBlur,
  initialContent,
}: {
  onBlur: (content: RemirrorJSON) => void;
  initialContent: RemirrorContentType | undefined;
}) {
  const { getJSON } = useHelpers();
  const previousJSON = useRef<RemirrorContentType | undefined>(initialContent);

  const handleOnBlur = useCallback(() => {
    const currentJSON = getJSON();
    const hasChanges = !isEqual(previousJSON.current, currentJSON);
    if (hasChanges) {
      onBlur(currentJSON);
      previousJSON.current = currentJSON;
    }
  }, [getJSON, onBlur]);

  useEditorEvent("blur", handleOnBlur);

  return <EditorComponent />;
}
